import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cx, sizeClass, type Hue } from './shared';

export type TabsVariant = 'underline' | 'glow' | 'bordered';

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** Shown as a count pill after the label. */
  count?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** underline (default), glow (PUBLIC nav) or bordered (games). */
  variant?: TabsVariant;
  hue?: Hue;
  size?: 'sm' | 'base' | 'lg' | 'xl';
  fill?: boolean;
  center?: boolean;
  /** Content of the selected tab. Omit to render just the tab list. */
  children?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

/** Tabs with the ARIA tabs pattern: arrow keys, Home and End move between tabs. */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  variant = 'underline',
  hue,
  size,
  fill,
  center,
  children,
  className,
  ...aria
}: TabsProps<T>) {
  const id = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: KeyboardEvent) => {
    const enabled = items.filter((item) => !item.disabled);
    const index = enabled.findIndex((item) => item.value === value);
    const next =
      e.key === 'ArrowRight' ? enabled[(index + 1) % enabled.length]
      : e.key === 'ArrowLeft' ? enabled[(index - 1 + enabled.length) % enabled.length]
      : e.key === 'Home' ? enabled[0]
      : e.key === 'End' ? enabled[enabled.length - 1]
      : undefined;
    if (!next) return;
    e.preventDefault();
    onChange(next.value);
    listRef.current?.querySelector<HTMLElement>(`[data-value="${next.value}"]`)?.focus();
  };

  return (
    <div
      className={cx(
        'tabs',
        variant !== 'underline' && `tabs--${variant}`,
        hue && `tabs--${hue}`,
        sizeClass('tabs', size),
        fill && 'tabs--fill',
        center && 'tabs--center',
        className,
      )}
    >
      <div className="tabs__list" role="tablist" ref={listRef} onKeyDown={onKeyDown} {...aria}>
        {items.map((item) => {
          const selected = item.value === value;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              id={`${id}-tab-${item.value}`}
              data-value={item.value}
              className={cx('tabs__tab', selected && 'is-active')}
              aria-selected={selected}
              aria-controls={children ? `${id}-panel` : undefined}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              onClick={() => onChange(item.value)}
            >
              {item.label}
              {item.count !== undefined && <span className="tabs__count">{item.count}</span>}
            </button>
          );
        })}
      </div>
      {children && (
        <div className="tabs__panel" role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${value}`}>
          {children}
        </div>
      )}
    </div>
  );
}
